import { createTheme, PaletteColorOptions } from '@mui/material/styles';
// Import Poppins font (if using @fontsource)
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
// 1. Define custom palette colors (optional, but good practice for brand colors)
declare module '@mui/material/styles' {
  interface Palette {
    pending: PaletteColorOptions;
  }
  interface PaletteOptions {
    pending?: PaletteColorOptions;
  }
}



// Theme colors inferred from the CRM Admin Dashboard template
const brandPrimary = '#7047ee';
const brandPrimaryLight = '#8b69f0';
const brandPrimaryDark = '#4d2fd6';
const brandSecondary = '#e9eef6';
const brandSuccess = '#00C389';
const brandSuccessLight = '#E6FFF2';
const brandError = '#FF4D6D';
const brandErrorLight = '#FFE6E6';
const brandWarning = '#FFB020';
const brandInfo = '#2F80FF';
const brandBackground = '#F6F8FB';
const brandPaper = '#FFFFFF';
const brandDarkText = '#0B2545';
const brandMidText = '#5C607B';

const theme = createTheme({
  // ------------------------------------
  // 2. PALETTE
  // ------------------------------------
  palette: {
    mode: 'light',
    primary: {
      main: brandPrimary,
      light: brandPrimaryLight,
      dark: brandPrimaryDark,
      contrastText: '#fff',
    },
    secondary: {
      main: brandSecondary,
      contrastText: brandDarkText,
    },
    info: {
      main: brandInfo,
      contrastText: '#fff',
    },
    success: {
      main: brandSuccess,
      light: brandSuccessLight,
      contrastText: brandDarkText,
    },
    error: {
      main: brandError,
      light: brandErrorLight,
      contrastText: '#fff',
    },
    warning: {
      main: brandWarning,
      contrastText: brandDarkText,
    },
    pending: {
      main: brandError,
      light: brandErrorLight,
    },
    background: {
      default: brandBackground,
      paper: brandPaper,
    },
    text: {
      primary: brandDarkText,
      secondary: brandMidText,
    },
  },

  // ------------------------------------
  // 3. TYPOGRAPHY - Updated with Poppins as primary font
  // ------------------------------------
  typography: {
    fontFamily: [
      'Poppins',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      color: brandDarkText,
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.75rem',
      color: brandDarkText,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: brandDarkText,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: brandDarkText,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: brandDarkText,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: brandDarkText,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      color: brandDarkText,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      color: brandMidText,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'none',
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
    },
    overline: {
      fontWeight: 500,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08333em',
    },
  },

  // ------------------------------------
  // 4. SHAPE & SPACING
  // ------------------------------------
  shape: {
    borderRadius: 6,
  },
  
  // ------------------------------------
  // 5. COMPONENT OVERRIDES
  // ------------------------------------
  components: {
    // A. General Container/Card Styles
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: 6,
        },
      },
    },
    
    // B. Button Styles
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#9A3063',
          },
        },
      },
    },

    // C. Input Field Styles
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: '#FFFFFF',
          fontFamily: "Poppins, sans-serif",
          '& fieldset': {
            borderColor: '#E0E0E0',
          },
        },
      },
    },

    // D. Custom Status Badge
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const baseStyle = {
            height: '24px',
            borderRadius: 4,
            fontFamily: "Poppins, sans-serif",
            '& .MuiChip-label': {
              padding: '0 8px',
              ...theme.typography.caption,
            },
          };
          if (ownerState.color === 'success') {
            return {
              ...baseStyle,
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.main,
            };
          }
          if (ownerState.className && ownerState.className.includes('MuiChip-pending')) {
            return {
              ...baseStyle,
              backgroundColor: (theme.palette as any).pending?.light || '#FFE6E6',
              color: (theme.palette as any).pending?.main || '#D32F2F',
            };
          }
          return baseStyle;
        },
      },
    },

    // E. Sidebar
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          margin: theme.spacing(0.5, 2),
          paddingLeft: theme.spacing(2),
          fontFamily: "Poppins, sans-serif",
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.primary.main,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
            },
          },
        }),
      },
    },

    // F. Typography components
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: "Poppins, sans-serif",
        },
      },
    },

    // G. App Bar
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: "Poppins, sans-serif",
        },
      },
    },

    // H. Toolbar
    MuiToolbar: {
      styleOverrides: {
        root: {
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
  },
});

export default theme;